import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { FiX } from "react-icons/fi";
import { LuRuler } from "react-icons/lu";
import Dimenzije from "../../assets/images/dimenzije.png";

const MEASUREMENTS = [
  { letter: "A", name: "Širina grudi", desc: "pola obima, mereno po najširem delu" },
  { letter: "B", name: "Dužina majice", desc: "od vrha ramena do dna majice" },
  { letter: "C", name: "Dužina rukava", desc: "od ramena do kraja rukava" },
];

const TShirtSizeGuide = ({ isOpen, onClose, productDimensions }) => {
  const sizeData = Array.isArray(productDimensions) ? productDimensions : [];

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <Modal
            role="dialog"
            aria-modal="true"
            aria-label="Vodič za veličine"
            initial={{ scale: 0.96, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Header>
              <Title>
                <LuRuler size={17} />
                Vodič za veličine
              </Title>
              <CloseX type="button" onClick={onClose} aria-label="Zatvori">
                <FiX size={16} />
              </CloseX>
            </Header>

            <Body>
              <ImagePanel>
                <img src={Dimenzije} alt="Šema merenja majice" />
              </ImagePanel>

              <InfoColumn>
                <Legend>
                  {MEASUREMENTS.map((m) => (
                    <LegendItem key={m.letter}>
                      <LetterBadge>{m.letter}</LetterBadge>
                      <div>
                        <LegendName>{m.name}</LegendName>
                        <LegendDesc>{m.desc}</LegendDesc>
                      </div>
                    </LegendItem>
                  ))}
                </Legend>

                {sizeData.length > 0 ? (
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <th>Veličina</th>
                          <th>A - Grudi</th>
                          <th>B - Dužina</th>
                          <th>C - Rukav</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sizeData.map((row) => (
                          <tr key={row.size}>
                            <SizeCell>{row.size}</SizeCell>
                            <td>{row.chest} cm</td>
                            <td>{row.length} cm</td>
                            <td>{row.sleeve} cm</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                ) : (
                  <EmptyNote>
                    Dimenzije za ovaj proizvod još nisu unete.
                  </EmptyNote>
                )}
              </InfoColumn>
            </Body>

            <FootNote>
              Sve mere su date u centimetrima. Preporučujemo da proverite mere
              pre naručivanja.
            </FootNote>
          </Modal>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default TShirtSizeGuide;

// Styled components
const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Modal = styled(motion.div)`
  background: var(--bg-surface);
  border: 1px solid var(--border-strong);
  border-radius: 12px;
  width: 100%;
  max-width: 840px;
  max-height: 92vh;
  /* dvh: iOS Safari računa vh preko cele visine ekrana (ispod URL bara),
     pa bi dno modala bilo odsečeno */
  max-height: 92dvh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.4rem;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;

  @media (max-width: 760px) {
    padding: 0.85rem 1rem;
  }
`;

const Title = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  color: var(--text-primary);
  font-size: 1.05rem;
  font-weight: 700;

  svg {
    color: var(--brand);
    flex-shrink: 0;
  }
`;

const CloseX = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  color: #a6a6a6;
  border: 1px solid var(--border);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: var(--text-primary);
  }
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
  gap: 1.4rem;
  padding: 1.4rem;
  overflow-y: auto;

  @media (max-width: 760px) {
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
    padding: 1rem;
  }
`;

const ImagePanel = styled.div`
  align-self: start;
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  overflow: hidden;
  background: #fff;

  img {
    display: block;
    width: 100%;
    height: auto;
  }

  @media (max-width: 760px) {
    align-self: center;
    width: 100%;
    max-width: 340px;
    flex-shrink: 0;

    /* na malim ekranima slika ne sme da pojede ceo modal,
       tabela veličina je bitnija od ilustracije */
    img {
      max-height: 34vh;
      object-fit: contain;
    }
  }
`;

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  min-width: 0;
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
`;

const LetterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: var(--radius-control);
  background: var(--brand-dim);
  border: 1px solid rgba(255, 107, 53, 0.35);
  color: var(--brand);
  font-size: 0.8rem;
  font-weight: 700;
`;

const LegendName = styled.div`
  color: var(--text-primary);
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1.3;
`;

const LegendDesc = styled.div`
  color: var(--text-muted);
  font-size: 0.78rem;
  line-height: 1.4;
  margin-top: 0.1rem;
`;

const TableWrap = styled.div`
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-card);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    background: var(--bg-surface-2);
    color: var(--text-muted);
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    text-align: center;
    padding: 0.55rem 0.8rem;
    white-space: nowrap;

    &:first-child {
      text-align: left;
    }
  }

  td {
    color: var(--text-secondary);
    font-size: 0.88rem;
    text-align: center;
    padding: 0.5rem 0.8rem;
    border-top: 1px solid var(--border);
    white-space: nowrap;
  }

  tbody tr {
    transition: background 0.12s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.03);
    }
  }
`;

const SizeCell = styled.td`
  && {
    color: var(--text-primary);
    font-weight: 700;
    text-align: left;
  }
`;

const EmptyNote = styled.div`
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-card);
  padding: 1.2rem;
  color: var(--text-muted);
  font-size: 0.85rem;
  text-align: center;
`;

const FootNote = styled.div`
  padding: 0.85rem 1.4rem;
  border-top: 1px solid var(--border);
  color: var(--text-muted);
  font-size: 0.8rem;
  line-height: 1.5;
  flex-shrink: 0;

  @media (max-width: 760px) {
    padding: 0.75rem 1rem;
  }
`;
